// Edit-or-create modal for a Territory+ "my client" entry. Form fields
// mirror the schema in territory_plus_routes.py. The same modal is used
// for both freshly-added clients (no ``initial``) and edits.
//
// For CQC/Scotland-linked clients: the franchisee can override ANY field
// (the doc is a private snapshot, never written back to the public CQC
// dataset). The "View live CQC data" button opens a side-by-side popup
// of the current live values so they can compare/reset to source.
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, Loader2, Trash2, UserPlus, ExternalLink, Database, MailX, Mail, Megaphone, Check, BedDouble } from "lucide-react";


// Regulator-linked source values. Used by save() to decide whether a
// CREATE must go through the ``/portal/territory-plus/clients/mark-home``
// endpoint (which persists the ``(source, home_id)`` linkage) vs. the
// generic ``/clients`` custom-create endpoint (which hard-codes
// ``source="custom"`` / ``home_id=null``). Kept as a module-level
// constant so the same set drives the direct-CQC-lookup gating a few
// dozen lines below.
const REGULATOR_CLIENT_SOURCES = new Set(["cqc", "scotland", "wales", "ni"]);
import api from "@/lib/api";
import { cleanFormPayload, buildDirtyPatch } from "@/components/territory/territoryClientForm";
import MiniClientMap from "@/components/territory/MiniClientMap";
import { LEAD_STATUS_OPTIONS, TONE_STYLES, getLeadStatusMeta } from "@/lib/leadStatus";

const FIELDS = [
  { key: "name",              label: "Name *",            type: "text", required: true },
  { key: "address",           label: "Address",           type: "textarea" },
  { key: "postcode",          label: "Postcode",          type: "text", hint: "We'll drop a marker on the map automatically." },
  { key: "phone",             label: "Phone",             type: "text" },
  { key: "website",           label: "Website",           type: "text" },
  { key: "provider",          label: "Provider (if applicable)", type: "text" },
  { key: "manager",           label: "Manager",           type: "text" },
  { key: "email",             label: "Email",             type: "text" },
  { key: "latest_inspection", label: "Latest inspection", type: "text" },
  { key: "cqc_rating",        label: "CQC rating",        type: "text" },
];

const CONTACT_METHOD_OPTIONS = [
  { value: "phone",           label: "Phone" },
  { value: "email",           label: "Email" },
  { value: "in_person",       label: "In Person" },
  { value: "facebook",        label: "Facebook" },
  { value: "linkedin",        label: "LinkedIn" },
  { value: "website_enquiry", label: "Website Enquiry" },
  { value: "other",           label: "Other" },
];

export default function TerritoryClientModal({
  initial, onClose, onSaved, onDeleted,
  cqcSnapshot = null, marketingEnabled = false,
  // HQ notes (Feb 2026 → history refactor). Append-only audit trail
  // scoped to (franchisee_id, source, home_id). Passed in as an array
  // of entries (newest-first); each entry carries {id, note, updated_at,
  // updated_by, updated_by_name}. Admins get an editor + delete controls;
  // franchisees see the same list rendered read-only.
  hqEntries = [],
  hqNoteEditable = false,
  onHqNoteSave = null,      // async (source, home_id, note) => void
  onHqEntryDelete = null,   // async (entry_id) => void  (admin only)
  hqSource = null,          // "cqc" | "scotland" | "wales" | "ni"
  hqHomeId = null,          // required with hqSource
  // Admin impersonation — when set, save calls include
  // ``?franchisee_id=`` so the /portal/territory-plus/clients endpoints
  // know which franchisee's My Clients list to write to. Franchisee
  // callers leave this null; their franchisee_id is inferred from
  // their JWT server-side.
  adminFranchiseeId = null,
}) {
  const navigate = useNavigate();
  // Phase 2 (Feb-2026): CLIENT POOL detail panel now shows CQC
  // Service Types / Specialisms / Bed count for records whose linked
  // home falls outside the current sector-scoped /territory/homes
  // result (which is capped at 2000). We fall back to a direct
  // GET /territory/home/{source}/{home_id} lookup when the parent
  // didn't manage to hand us a full snapshot. Values are NEVER
  // persisted into franchisee_clients — always live from CQC.
  const [snapshotFallback, setSnapshotFallback] = useState(null);
  useEffect(() => {
    if (!initial) return;                       // "Add new" mode, nothing to fetch
    const src = initial.source;
    const hid = initial.home_id;
    if (!hid || !REGULATOR_CLIENT_SOURCES.has(src)) return;
    // Skip the extra network hop when the parent already handed us a
    // usable snapshot. "Usable" = at least one of the three CQC
    // fields we render is present — service types, specialisms, or
    // bed count.
    const usable = cqcSnapshot && (
      (cqcSnapshot.gacServiceTypes || []).length > 0
      || (cqcSnapshot.specialisms || []).length > 0
      || cqcSnapshot.numberOfBeds != null
    );
    if (usable) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/territory/home/${encodeURIComponent(src)}/${encodeURIComponent(hid)}`);
        if (!cancelled && data?.home) setSnapshotFallback(data.home);
      } catch (e) {
        // Silent — the modal must still open even if the lookup
        // fails (e.g. regulator collection down). Chips just don't
        // render, matching the pre-Phase-2 behaviour.
      }
    })();
    return () => { cancelled = true; };
  }, [initial, cqcSnapshot]);
  // Effective snapshot the modal renders against. Prefer the
  // parent-supplied one (fastest) and fall back to the direct lookup.
  const effectiveSnapshot = cqcSnapshot || snapshotFallback;
  // Build the initial (raw, uncleaned) form state from ``initial``.
  // Extracted into a helper so both ``form`` and the prefill
  // ``snapshotClean`` are derived from the exact same source of
  // truth — otherwise dirty-field diffs on the existing_client PATCH
  // path would false-positive on shape drift between the two.
  const buildInitialForm = () => {
    const empty = Object.fromEntries(FIELDS.map((f) => [f.key, ""]));
    // Default status: rows with no ``lead_status`` set (legacy records
    // or freshly-marked prospects) are treated as "Not Contacted"
    // everywhere — the My Clients list already falls back to this, so
    // the modal must too or the dropdown reads "— select status —"
    // while the row chip shouts NOT CONTACTED.
    if (!initial) return { ...empty, notes: "", contacts: [], lead_status: "not_contacted", last_contact_date: "", last_contact_method: "", follow_up_required: false, follow_up_date: "", follow_up_notes: "" };
    return {
      ...empty,
      last_contact_date: "",
      last_contact_method: "",
      follow_up_date: "",
      follow_up_notes: "",
      ...Object.fromEntries(
        Object.entries(initial)
          .filter(([k]) => k !== "follow_up_required")
          .map(([k, v]) => [k, v ?? ""]),
      ),
      // ``initial.lead_status`` may be missing or empty (older records)
      // — coerce to the canonical default so the dropdown matches the
      // list view's fallback chip.
      lead_status: initial.lead_status || "not_contacted",
      notes: initial.notes || "",
      contacts: Array.isArray(initial.contacts) ? initial.contacts : [],
      follow_up_required: !!initial.follow_up_required,
    };
  };
  const [form, setForm] = useState(buildInitialForm);
  // Cleaned prefill snapshot — captured at modal open. Used by
  // the existing_client PATCH path (Defect-3 forward fix) to send
  // ONLY the fields the user actually touched. Untouched blank seed
  // values compare equal to the snapshot and are stripped from the
  // PATCH body; deliberate clears (null / false / []) DO differ from
  // the snapshot and are transmitted so the row updates correctly.
  const [snapshotClean, setSnapshotClean] = useState(() => cleanFormPayload(buildInitialForm()));

  // Defensive identity-change guard: the modal is normally
  // remounted per home (parent renders it inside `{editingClient &&
  // ...}` and clears `editingClient` on close), so the useState
  // initializers above re-run for each fresh open. If a caller ever
  // swaps ``initial`` in-place without an unmount (e.g. a
  // "next client" affordance), the ``form`` and ``snapshotClean``
  // captured for the previous home would leak into the new one —
  // dirty-field diffs would false-positive and could overwrite the
  // wrong row. This effect resets both when the client/home
  // identity actually changes.
  const identityKey = initial
    ? `${initial.source || ""}:${initial.home_id || initial.id || ""}`
    : "__new__";
  const prevIdentityRef = useRef(identityKey);
  useEffect(() => {
    if (prevIdentityRef.current !== identityKey) {
      const fresh = buildInitialForm();
      setForm(fresh);
      setSnapshotClean(cleanFormPayload(fresh));
      prevIdentityRef.current = identityKey;
    }
    // ``buildInitialForm`` closes over ``initial``; ``identityKey``
    // is derived from it, so identity is the only dep we need.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [identityKey]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [showCqc, setShowCqc] = useState(false);
  // Quick-note ("Add a note…") state — mirrors the admin CRM pattern
  // so franchisees get immediate save feedback and a UK-local
  // ``[DD/MM/YYYY HH:MM]`` timestamp prepended to every fresh entry
  // in the running Notes log below. Only wired when editing an
  // existing client — brand-new clients still hit the modal's main
  // Save button.
  const [quickNoteSaving, setQuickNoteSaving] = useState(false);

  const editing = !!initial?.id;
  const isCustom = !initial || initial.source === "custom";
  const isLinked = !!initial && initial.source !== "custom";
  // A client only graduates from "Prospective" to "Client" once the
  // franchisee bumps Lead Status to "Regular Client" in the Marketing
  // panel. Drives the header label + the save-button copy below.
  const isRegularClient = (form.lead_status || "") === "regular_client";

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Persist a single quick-note against the client. Server appends
  // ``[DD/MM/YYYY HH:MM] <text>`` to the running ``notes`` log and
  // returns the fresh notes + ``notes_updated_at`` so the panel's
  // "Saved just now" indicator updates without a modal reload.
  // Called on Enter / blur from ``QuickNoteInput``.
  const handleQuickNoteSave = async (rawText) => {
    const text = (rawText || "").trim();
    if (!text || !editing || !initial?.id) return { ok: false };
    setQuickNoteSaving(true);
    setErr("");
    try {
      const params = adminFranchiseeId ? { franchisee_id: adminFranchiseeId } : undefined;
      const { data } = await api.patch(
        `/portal/territory-plus/clients/${initial.id}`,
        { quick_note: text },
        { params },
      );
      // Server returns the full shaped doc — merge notes + timestamp
      // back into the form so the running log below updates live.
      setForm((f) => ({
        ...f,
        notes: data?.notes ?? f.notes,
        notes_updated_at: data?.notes_updated_at ?? f.notes_updated_at,
      }));
      onSaved?.(data);
      return { ok: true };
    } catch (e) {
      setErr(e?.response?.data?.detail || "Couldn't save the note.");
      return { ok: false };
    } finally {
      setQuickNoteSaving(false);
    }
  };

  // Toggle the per-contact marketing-unsubscribed flag. The persistence
  // is a separate single-purpose endpoint (rather than rolled into the
  // main save) so the franchisee can toggle it without first having
  // to fix any other validation errors on the form.
  const toggleUnsubscribed = async (contactIndex, currentlyUnsub) => {
    if (!editing || !initial?.id) return;
    setBusy(true); setErr("");
    try {
      await api.post(`/portal/marketing/clients/${initial.id}/unsubscribe`, {
        contact_index: contactIndex,
        unsubscribed: !currentlyUnsub,
      });
      // Mirror the change into local form state so the UI updates
      // immediately without an extra round-trip.
      if (contactIndex === -1) {
        setForm((f) => ({ ...f, primary_marketing_unsubscribed: !currentlyUnsub }));
      } else {
        setForm((f) => {
          const next = [...(f.contacts || [])];
          if (next[contactIndex]) {
            next[contactIndex] = {
              ...next[contactIndex],
              marketing_unsubscribed: !currentlyUnsub,
            };
          }
          return { ...f, contacts: next };
        });
      }
    } catch (e) {
      setErr(e?.response?.data?.detail || "Couldn't update marketing status.");
    } finally {
      setBusy(false);
    }
  };

  const updateContact = (i, k, v) => {
    setForm((f) => {
      const next = [...(f.contacts || [])];
      next[i] = { ...next[i], [k]: v };
      return { ...f, contacts: next };
    });
  };
  const addContact = () => {
    setForm((f) => ({
      ...f,
      contacts: [...(f.contacts || []), { name: "", role: "", phone: "", email: "", notes: "" }],
    }));
  };
  const removeContact = (i) => {
    setForm((f) => ({ ...f, contacts: (f.contacts || []).filter((_, idx) => idx !== i) }));
  };

  const save = async (e) => {
    e?.preventDefault?.();
    setErr("");
    if (!form.name.trim()) { setErr("Name is required."); return; }
    setBusy(true);
    try {
      // Normalise current form → on-the-wire body (strings trimmed,
      // empty strings → null, empty contact rows dropped, contact
      // shape stabilised). Same transform is applied to the prefill
      // snapshot at modal-open time so the dirty-field diff below
      // compares apples to apples.
      const body = cleanFormPayload(form);
      const params = adminFranchiseeId ? { franchisee_id: adminFranchiseeId } : undefined;
      let res;
      if (editing) {
        res = await api.patch(`/portal/territory-plus/clients/${initial.id}`, body, { params });
      } else if (
        // Feb-2026 fix: when the modal was seeded from a Territory Pool
        // regulator home, the CREATE must go through the mark-home
        // endpoint so the ``(franchisee_id, source, home_id)`` triple
        // is stored — otherwise the generic /clients endpoint drops
        // both fields to ``"custom" / null`` and the Territory Pool
        // exclusion + Client Pool CQC lookup both silently fail.
        //
        // We keep every field the user just typed by immediately
        // PATCHing the newly-created record with the full form body —
        // mark-home itself only accepts a regulator-safe subset (name,
        // address, phone, provider, manager, notes, postcode, lat,
        // lng). PATCH covers everything else (email, cqc_rating,
        // latest_inspection, contacts[], lead_status, last_contact_*,
        // follow_up_*, marketing flags).
        initial
        && REGULATOR_CLIENT_SOURCES.has(initial.source)
        && initial.home_id
      ) {
        const markBody = {
          source: initial.source,
          home_id: initial.home_id,
          name: body.name,
          address: body.address,
          phone: body.phone,
          website: body.website,
          provider: body.provider,
          manager: body.manager,
          notes: body.notes,
          postcode: body.postcode,
          lat: initial.lat ?? null,
          lng: initial.lng ?? null,
        };
        const markRes = await api.post(
          "/portal/territory-plus/clients/mark-home", markBody, { params },
        );
        // Defect-3 fix (commit [18]): mark-home is idempotent, so a
        // ``status: "existing_client"`` response used to close the
        // modal without ever PATCHing — silently dropping every
        // marketing / CRM field the user just typed. We now PATCH
        // the existing row with the same full body used on the
        // CREATE-success branch below, verify the echo contains
        // every field we submitted, and only then emit onSaved /
        // onClose. On any PATCH failure or missing-echo we keep
        // the modal open and surface the error.
        const existingId = markRes?.data?.status === "existing_client"
          ? markRes.data.existing_id
          : null;
        const targetId = existingId ?? markRes?.data?.id;
        // Preservation guard for the existing_client path only —
        // dirty-field tracking (Defect-3 minimal correction, Feb 2026):
        // Territory Pool seeds a fresh form from the regulator-side
        // row which has no CRM/marketing state, so untouched keys
        // arrive in `body` as null / empty-array after the cleaning
        // pass. Sending those verbatim via PATCH would $set-overwrite
        // the row's existing populated CRM fields with blanks.
        //
        // We diff `body` against `snapshotClean` (the cleaned prefill
        // captured on modal-open) and include ONLY fields the user
        // actually touched. Untouched blanks are omitted so the
        // backend preserves the existing values; deliberate clears
        // — a populated field explicitly emptied, a boolean flipped
        // to false, or the final contact removed (contacts: []) —
        // DO differ from the snapshot and are transmitted so the
        // row updates correctly.
        const patchBody = existingId
          ? buildDirtyPatch(body, snapshotClean)
          : body;
        res = await api.patch(
          `/portal/territory-plus/clients/${targetId}`, patchBody, { params },
        );
        // Value-based echo verification (not merely key presence):
        // for every field we sent, the persisted value on the echo
        // must equal what we submitted. A stale / stripped value
        // means the write did not land as expected — keep the modal
        // open and surface the diverging fields.
        const echoData = res?.data || {};
        const mismatched = Object.keys(patchBody).filter((k) => {
          if (patchBody[k] === undefined) return false;
          return JSON.stringify(echoData[k]) !== JSON.stringify(patchBody[k]);
        });
        if (mismatched.length > 0) {
          setErr(
            `Saved partially — these fields did not persist as submitted: `
            + `${mismatched.join(", ")}. Please retry.`
          );
          return; // modal stays open; no onSaved/onClose
        }
        if (existingId) {
          onSaved?.({ ...res.data, _mergedIntoExisting: true });
          onClose?.();
          return;
        }
      } else {
        res = await api.post("/portal/territory-plus/clients", body, { params });
      }
      onSaved?.(res.data);
      onClose?.();
    } catch (e2) {
      // Wave-1 duplicate-prevention responses (409) carry a structured
      // body so the UI can distinguish between "one existing match" and
      // "multiple existing matches" and NEVER silently open an arbitrary
      // record when the backend reports ambiguity.
      const status = e2?.response?.status;
      const detail = e2?.response?.data?.detail;
      if (status === 409 && detail && typeof detail === "object") {
        if (detail.status === "duplicate_client" && detail.existing_id) {
          setErr("This client already exists in your list. Opening the existing record...");
          onSaved?.({ id: detail.existing_id, _openedExisting: true });
          onClose?.();
        } else if (detail.status === "duplicate_client_ambiguous") {
          setErr(
            "This client already appears more than once in the database (" +
            (detail.candidate_ids || []).length +
            " matching records). An admin needs to reconcile the duplicates before you can add another entry."
          );
        } else if (detail.status === "identity_creation_in_progress") {
          setErr("This client is being created by another request — please try again in a moment.");
        } else {
          setErr(typeof detail === "string" ? detail : (detail?.message || "Couldn't save the client."));
        }
      } else {
        setErr(typeof detail === "string" ? detail : "Couldn't save the client.");
      }
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!editing) return;
    if (!window.confirm(`Remove ${initial.name} from your clients?`)) return;
    setBusy(true); setErr("");
    try {
      if (initial.source === "custom") {
        await api.delete(`/portal/territory-plus/clients/${initial.id}`);
      } else {
        // Regulated home → unmark via the mark-home endpoint.
        await api.delete(`/portal/territory-plus/clients/mark-home`, {
          data: { source: initial.source, home_id: initial.home_id },
        });
      }
      onDeleted?.(initial);
      onClose?.();
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Couldn't remove the client.");
    } finally {
      setBusy(false);
    }
  };

  // Esc to close.
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-5 sm:p-6 bg-stone-950/60 backdrop-blur-sm"
      onClick={onClose}
      data-testid="t-plus-client-modal"
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[92vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 sm:px-7 py-4 border-b border-stone-200 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-stone-500">
              {isRegularClient
                ? (editing ? "My client" : "Add a client")
                : (editing ? "My prospective client" : "Add a prospective client")}
            </div>
            <h2 className="font-display text-xl font-black text-stone-950 truncate">
              {editing ? (form.name || "—") : (isRegularClient ? "New client" : "New prospect")}
            </h2>
          </div>
          <button onClick={onClose} data-testid="t-plus-client-close" className="p-2 -mr-1 text-stone-600 hover:bg-stone-100 rounded-lg shrink-0">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={save} className="flex-1 overflow-y-auto px-6 sm:px-7 py-5 space-y-4">
          {isLinked && (
            <div className="px-3 py-2.5 bg-amber-50 border border-amber-200 text-amber-900 text-xs rounded-lg flex items-start gap-3">
              <div className="flex-1">
                This client started from <strong>{
                  initial.source === "scotland" ? "Care Inspectorate Scotland"
                  : initial.source === "wales" ? "Care Inspectorate Wales (CIW)"
                  : initial.source === "ni" ? "RQIA (Northern Ireland)"
                  : "CQC"
                }</strong>.
                You can edit any field — your changes are private to you and never written
                back to the public dataset.
              </div>
              {isLinked && (
                <button
                  type="button"
                  onClick={() => setShowCqc(true)}
                  data-testid="t-plus-view-cqc"
                  disabled={!effectiveSnapshot}
                  className="shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1.5 text-[10px] font-bold uppercase tracking-wider bg-stone-950 text-white hover:bg-stone-800 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Database className="w-3 h-3" /> View live CQC data
                </button>
              )}
            </div>
          )}
          {err && (
            <div className="px-3 py-2 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg">{err}</div>
          )}

          {/* TWO-COLUMN GRID — fields on the left, map + marketing on the right.
              Collapses to a single column under lg: so phones still get the
              same form-then-map-then-marketing flow.                              */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
            {/* LEFT COLUMN — name, address, contact details, notes, contacts */}
            <div className="lg:col-span-7 space-y-4 min-w-0">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {FIELDS.map((f) => (
                  <div key={f.key} className={f.type === "textarea" ? "sm:col-span-2" : ""}>
                    <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-stone-600 mb-1 block">{f.label}</label>
                    {f.type === "textarea" ? (
                      <textarea
                        value={form[f.key] ?? ""}
                        onChange={(e) => set(f.key, e.target.value)}
                        rows={2}
                        data-testid={`t-plus-field-${f.key}`}
                        className="w-full px-3 py-2 text-sm bg-stone-50 border border-stone-300 rounded-lg focus:outline-none focus:border-stone-950"
                      />
                    ) : (
                      <input
                        type="text"
                        value={form[f.key] ?? ""}
                        onChange={(e) => set(f.key, e.target.value)}
                        required={f.required}
                        data-testid={`t-plus-field-${f.key}`}
                        className="w-full px-3 py-2 text-sm bg-stone-50 border border-stone-300 rounded-lg focus:outline-none focus:border-stone-950"
                      />
                    )}
                    {f.hint && <div className="text-[11px] text-stone-500 mt-1">{f.hint}</div>}
                  </div>
                ))}
              </div>

              {/* CQC Service Type + Specialisms + Bed count — read-only
                  chips sourced from the live CQC snapshot for this
                  location. Rendered directly in the main details area
                  (not behind the "View live CQC data" popup) so
                  franchisees and admins see the care-market context at
                  a glance when opening a prospective/current client.
                  Hidden for custom clients / rows with no snapshot.

                  Phase 2 (Feb-2026): also reads from the direct
                  ``/territory/home/{source}/{home_id}`` lookup so
                  CLIENT POOL entries outside the current sector-scoped
                  homes list still surface these fields. Values are
                  NEVER persisted to franchisee_clients. */}
              {effectiveSnapshot && ((effectiveSnapshot.gacServiceTypes || []).length > 0
                             || (effectiveSnapshot.specialisms || []).length > 0
                             || effectiveSnapshot.numberOfBeds != null) && (
                <div className="rounded-lg border border-stone-200 bg-stone-50/60 p-3 space-y-2" data-testid="t-plus-cqc-services-block">
                  {(effectiveSnapshot.gacServiceTypes || []).length > 0 && (
                    <div data-testid="t-plus-service-types">
                      <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-stone-600 mb-1.5">Service Type</div>
                      <div className="flex flex-wrap gap-1">
                        {(effectiveSnapshot.gacServiceTypes || []).map((st) => st?.name).filter(Boolean).map((n) => (
                          <span key={n} className="px-2 py-0.5 bg-blue-100 text-blue-900 text-[11px] rounded-md border border-blue-200">{n}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  {(effectiveSnapshot.specialisms || []).length > 0 && (
                    <div data-testid="t-plus-specialisms">
                      <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-stone-600 mb-1.5">Specialisms</div>
                      <div className="flex flex-wrap gap-1">
                        {(effectiveSnapshot.specialisms || []).map((s) => s?.name).filter(Boolean).map((n) => (
                          <span key={n} className="px-2 py-0.5 bg-stone-200/70 text-stone-800 text-[11px] rounded-md">{n}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  {effectiveSnapshot.numberOfBeds != null && (
                    <div data-testid="t-plus-number-of-beds">
                      <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-stone-600 mb-1.5">Number of Beds</div>
                      <div className="flex flex-wrap gap-1">
                        <span className="px-2 py-0.5 bg-emerald-100 text-emerald-900 text-[11px] rounded-md border border-emerald-200 inline-flex items-center gap-1">
                          <BedDouble className="w-3 h-3" /> {effectiveSnapshot.numberOfBeds}
                        </span>
                      </div>
                    </div>
                  )}
                  <div className="text-[10px] text-stone-500 italic pt-0.5">Read-only from CQC — updates automatically when CQC refreshes.</div>
                </div>
              )}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-stone-600 block">Notes</label>
                  {editing && (
                    <QuickNoteSavedIndicator savedAt={form.notes_updated_at} saving={quickNoteSaving} />
                  )}
                </div>
                {editing && (
                  <QuickNoteInput
                    saving={quickNoteSaving}
                    onSave={handleQuickNoteSave}
                  />
                )}
                <textarea
                  value={form.notes || ""}
                  onChange={(e) => set("notes", e.target.value)}
                  rows={editing ? 5 : 3}
                  data-testid="t-plus-field-notes"
                  className="w-full px-3 py-2 text-sm bg-stone-50 border border-stone-300 rounded-lg focus:outline-none focus:border-stone-950"
                />
              </div>

              {/* HQ notes — append-only history with controlled admin
                  deletion. Editable on the admin franchisee page; the
                  same list renders read-only on the franchisee portal.
                  Rendered even for basic MyTerritory franchisees
                  because notes live against the CQC entry itself (no
                  franchisee_clients doc required). The parent widget
                  resolves the canonical (source, home_id) reference
                  and passes it in — we no longer fall back to
                  ``initial?.source`` / ``initial?.home_id`` here because
                  those fields diverge across CQC vs custom vs seeded
                  records and were the root of the "Missing home
                  reference" bug on manually-created clients. */}
              {(hqNoteEditable || (hqEntries && hqEntries.length > 0)) && hqSource && hqHomeId && (
                <HqNoteSection
                  hqEntries={hqEntries}
                  editable={hqNoteEditable}
                  onSave={onHqNoteSave}
                  onEntryDelete={onHqEntryDelete}
                  source={hqSource}
                  homeId={hqHomeId}
                />
              )}

              {(form.email || "").trim() && (
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.manager_include_for_marketing !== false}
                    onChange={(e) => set("manager_include_for_marketing", e.target.checked)}
                    data-testid="t-plus-primary-marketing"
                    className="w-4 h-4 rounded border-stone-300 accent-stone-950"
                  />
                  <span className="text-stone-700">Include manager / primary contact in Marketing+ e-shots</span>
                </label>
              )}
              {editing && (form.email || "").trim() && (
                <UnsubscribeRow
                  label="Primary contact marketing"
                  email={form.email}
                  unsubscribed={!!form.primary_marketing_unsubscribed}
                  unsubscribedAt={form.primary_marketing_unsubscribed_at}
                  source={form.primary_marketing_unsubscribed_source}
                  onToggle={() => toggleUnsubscribed(-1, !!form.primary_marketing_unsubscribed)}
                  busy={busy}
                  testid="t-plus-primary-unsub"
                />
              )}

              {/* Additional contacts moved into the left column */}
              <div data-testid="t-plus-contacts-section">
                <div className="flex items-center justify-between gap-2 mb-2">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-stone-600">Additional contacts</div>
                    <div className="text-[11px] text-stone-500">Extra people you deal with at this client beyond the main manager.</div>
                  </div>
                  <button
                    type="button"
                    onClick={addContact}
                    data-testid="t-plus-add-contact"
                    className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-[10px] font-bold uppercase tracking-wider bg-stone-100 hover:bg-stone-200 text-stone-900 rounded-md border border-stone-300"
                  >
                    <UserPlus className="w-3.5 h-3.5" /> Add contact
                  </button>
                </div>
                {(form.contacts || []).length === 0 && (
                  <div className="px-4 py-5 text-center text-[11px] text-stone-500 bg-stone-50 border border-dashed border-stone-300 rounded-lg">
                    No additional contacts yet.
                  </div>
                )}
                <div className="space-y-2">
                  {(form.contacts || []).map((c, i) => (
                    <div key={i} className="bg-stone-50 border border-stone-200 rounded-lg p-3" data-testid={`t-plus-contact-row-${i}`}>
                      <div className="flex items-center justify-between gap-2 mb-2">
                        <div className="text-[10px] uppercase tracking-wider font-bold text-stone-500">Contact #{i + 1}</div>
                        <button
                          type="button"
                          onClick={() => removeContact(i)}
                          data-testid={`t-plus-remove-contact-${i}`}
                          className="p-1 text-stone-500 hover:text-red-700 hover:bg-red-50 rounded"
                          aria-label="Remove contact"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        <input
                          type="text"
                          value={c.name || ""}
                          onChange={(e) => updateContact(i, "name", e.target.value)}
                          placeholder="Name"
                          data-testid={`t-plus-contact-name-${i}`}
                          className="px-2.5 py-1.5 text-sm bg-white border border-stone-300 rounded focus:outline-none focus:border-stone-950"
                        />
                        <input
                          type="text"
                          value={c.role || ""}
                          onChange={(e) => updateContact(i, "role", e.target.value)}
                          placeholder="Role (e.g. Deputy Manager)"
                          data-testid={`t-plus-contact-role-${i}`}
                          className="px-2.5 py-1.5 text-sm bg-white border border-stone-300 rounded focus:outline-none focus:border-stone-950"
                        />
                        <input
                          type="text"
                          value={c.phone || ""}
                          onChange={(e) => updateContact(i, "phone", e.target.value)}
                          placeholder="Phone"
                          data-testid={`t-plus-contact-phone-${i}`}
                          className="px-2.5 py-1.5 text-sm bg-white border border-stone-300 rounded focus:outline-none focus:border-stone-950"
                        />
                        <div className="flex flex-col gap-1">
                          <input
                            type="text"
                            value={c.email || ""}
                            onChange={(e) => updateContact(i, "email", e.target.value)}
                            placeholder="Email"
                            data-testid={`t-plus-contact-email-${i}`}
                            className="px-2.5 py-1.5 text-sm bg-white border border-stone-300 rounded focus:outline-none focus:border-stone-950"
                          />
                          {(c.email || "").trim() && (
                            <label className="flex items-center gap-1.5 text-[11px] text-stone-600 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={c.include_for_marketing !== false}
                                onChange={(e) => updateContact(i, "include_for_marketing", e.target.checked)}
                                data-testid={`t-plus-contact-marketing-${i}`}
                                className="w-3.5 h-3.5 rounded border-stone-300 accent-stone-950"
                              />
                              Include contact for e-shot marketing
                            </label>
                          )}
                        </div>
                      </div>
                      <textarea
                        value={c.notes || ""}
                        onChange={(e) => updateContact(i, "notes", e.target.value)}
                        placeholder="Notes about this contact (optional)"
                        rows={2}
                        data-testid={`t-plus-contact-notes-${i}`}
                        className="mt-2 w-full px-2.5 py-1.5 text-sm bg-white border border-stone-300 rounded focus:outline-none focus:border-stone-950"
                      />
                      {editing && (c.email || "").trim() && (
                        <div className="mt-2">
                          <UnsubscribeRow
                            label="Marketing"
                            email={c.email}
                            unsubscribed={!!c.marketing_unsubscribed}
                            unsubscribedAt={c.marketing_unsubscribed_at}
                            source={c.marketing_unsubscribed_source}
                            onToggle={() => toggleUnsubscribed(i, !!c.marketing_unsubscribed)}
                            busy={busy}
                            testid={`t-plus-contact-unsub-${i}`}
                            compact
                          />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* RIGHT COLUMN — map (taller) + Marketing CRM panel */}
            <div className="lg:col-span-5 space-y-4 min-w-0">
              {(() => {
                const lat = cqcSnapshot?.latitude ?? initial?.lat ?? null;
                const lng = cqcSnapshot?.longitude ?? initial?.lng ?? null;
                return (
                  <MiniClientMap
                    lat={lat}
                    lng={lng}
                    label={form.name}
                    postcode={form.postcode || cqcSnapshot?.postalCode || ""}
                    heightClass="h-72"
                  />
                );
              })()}

              <MarketingCrmPanel
                form={form}
                set={set}
                marketingEnabled={marketingEnabled}
                onOpenMarketingPlus={() => {
                  // Deep-link to Marketing+ with this client pre-selected.
                  // Only meaningful for saved clients (need an id).
                  if (initial?.id) {
                    navigate(`/portal/marketing?client_id=${encodeURIComponent(initial.id)}`);
                    onClose?.();
                  }
                }}
                clientSaved={!!initial?.id}
              />
            </div>
          </div>
        </form>

        <div className="px-6 sm:px-7 py-4 border-t border-stone-200 flex items-center justify-between gap-3">
          <div>
            {editing && (
              <button
                type="button"
                onClick={remove}
                disabled={busy}
                data-testid="t-plus-client-delete"
                className="px-3 py-2 text-xs font-bold uppercase tracking-wider bg-red-50 hover:bg-red-100 text-red-700 rounded-lg flex items-center gap-1.5"
              >
                <Trash2 className="w-3.5 h-3.5" /> {initial.source === "custom" ? "Delete client" : "Remove flag"}
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button type="button" onClick={onClose} className="px-3 py-2 text-xs font-bold uppercase tracking-wider text-stone-700 hover:bg-stone-100 rounded-lg">Cancel</button>
            <button
              type="button"
              onClick={save}
              disabled={busy}
              data-testid="t-plus-client-save"
              className="px-4 py-2 text-xs font-bold uppercase tracking-wider bg-stone-950 text-white hover:bg-stone-800 rounded-lg flex items-center gap-1.5 disabled:opacity-50"
            >
              {busy
                ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                : (isRegularClient
                    ? (editing ? "Update Client" : "Save Client")
                    : (editing ? "Update Prospective Client" : "Save Prospective Client"))}
            </button>
          </div>
        </div>
      </div>

      {showCqc && cqcSnapshot && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-stone-950/60 backdrop-blur-sm"
          onClick={() => setShowCqc(false)}
          data-testid="t-plus-cqc-popup"
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[85vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 py-4 border-b border-stone-200 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-stone-500">
                  Live data from {
                    initial?.source === "scotland" ? "Care Inspectorate Scotland"
                    : initial?.source === "wales" ? "Care Inspectorate Wales (CIW)"
                    : initial?.source === "ni" ? "RQIA (Northern Ireland)"
                    : "CQC"
                  }
                </div>
                <h3 className="font-display text-lg font-black text-stone-950 truncate">{cqcSnapshot.name || "—"}</h3>
              </div>
              <button
                onClick={() => setShowCqc(false)}
                data-testid="t-plus-cqc-close"
                className="p-2 -mr-1 text-stone-600 hover:bg-stone-100 rounded-lg shrink-0"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-2.5 text-sm">
              {[
                ["Name", cqcSnapshot.name],
                ["Address", cqcSnapshot.fullAddress
                  || [cqcSnapshot.postalAddressLine1, cqcSnapshot.postalAddressLine2, cqcSnapshot.postalAddressTownCity, cqcSnapshot.postalAddressCounty, cqcSnapshot.postalCode]
                      .filter(Boolean).join(", ")],
                ["Postcode", cqcSnapshot.postalCode || cqcSnapshot.postcode],
                ["Phone", cqcSnapshot.mainPhoneNumber],
                ["Website", cqcSnapshot.website],
                ["Provider", cqcSnapshot.providerName],
                ["Manager", cqcSnapshot.registrationManagerName],
                ["CQC rating", cqcSnapshot.currentRatings?.overall?.rating],
                ["Latest inspection", cqcSnapshot.lastInspection?.date || cqcSnapshot.currentRatings?.overall?.reportDate],
                ["Number of beds", cqcSnapshot.numberOfBeds],
              ].map(([label, value]) => (
                <div key={label} className="grid grid-cols-3 gap-3 py-1.5 border-b border-stone-100 last:border-b-0">
                  <div className="text-[10px] uppercase tracking-wider font-bold text-stone-500">{label}</div>
                  <div className="col-span-2 text-stone-900 break-words">
                    {value || <span className="text-stone-400 italic">Not on file</span>}
                  </div>
                </div>
              ))}
              {/* Service Type + Specialisms — read-only chips sourced
                  from the same CQC snapshot as the fields above. Kept
                  outside the [label, value] mapper because they render
                  as chip lists, not single-line values. */}
              {(cqcSnapshot.gacServiceTypes || []).length > 0 && (
                <div className="py-1.5 border-b border-stone-100" data-testid="cqc-live-service-types">
                  <div className="text-[10px] uppercase tracking-wider font-bold text-stone-500 mb-1">Service Type</div>
                  <div className="flex flex-wrap gap-1">
                    {(cqcSnapshot.gacServiceTypes || []).map((st) => st?.name).filter(Boolean).map((n) => (
                      <span key={n} className="px-2 py-0.5 bg-blue-100 text-blue-900 text-[11px] rounded-md border border-blue-200">{n}</span>
                    ))}
                  </div>
                </div>
              )}
              {(cqcSnapshot.specialisms || []).length > 0 && (
                <div className="py-1.5" data-testid="cqc-live-specialisms">
                  <div className="text-[10px] uppercase tracking-wider font-bold text-stone-500 mb-1">Specialisms</div>
                  <div className="flex flex-wrap gap-1">
                    {(cqcSnapshot.specialisms || []).map((s) => s?.name).filter(Boolean).map((n) => (
                      <span key={n} className="px-2 py-0.5 bg-stone-200/60 text-stone-800 text-[11px] rounded-md">{n}</span>
                    ))}
                  </div>
                </div>
              )}
              {cqcSnapshot.locationURL && (
                <a href={cqcSnapshot.locationURL} target="_blank" rel="noreferrer"
                  className="mt-3 inline-flex items-center gap-1.5 px-3 py-2 text-[10px] font-bold uppercase tracking-wider border border-stone-300 hover:bg-stone-50 rounded-md text-stone-900">
                  <ExternalLink className="w-3 h-3" /> Open on CQC website
                </a>
              )}
            </div>
            <div className="px-5 py-3 border-t border-stone-200 text-[11px] text-stone-500">
              This is the unedited public record. Your overrides above are private to you.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Small reusable row that shows the current marketing status for an
// email + a single button to flip it. We deliberately don't put this
// in its own file because it's only used inside the client modal and
// shares the modal's `busy` / `onToggle` plumbing.
function UnsubscribeRow({ label, email, unsubscribed, unsubscribedAt,
                          source, onToggle, busy, testid, compact = false }) {
  const dateLabel = unsubscribedAt
    ? new Date(unsubscribedAt).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })
    : "";
  const sourceLabel = source === "recipient" ? "via one-click link"
                    : source === "franchisee" ? "by you"
                    : "";
  return (
    <div
      data-testid={testid}
      className={`flex items-center gap-3 ${compact ? "p-2 bg-stone-50" : "px-3 py-2.5 bg-amber-50/40"} border ${unsubscribed ? "border-red-200 bg-red-50/60" : "border-stone-200"} rounded-lg`}
    >
      <div className="shrink-0">
        {unsubscribed
          ? <span className="w-7 h-7 rounded-full bg-red-100 text-red-700 flex items-center justify-center"><MailX className="w-3.5 h-3.5" /></span>
          : <span className="w-7 h-7 rounded-full bg-stone-200 text-stone-700 flex items-center justify-center"><Mail className="w-3.5 h-3.5" /></span>}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[11px] font-bold uppercase tracking-wider text-stone-700">
          {label} — {unsubscribed ? "Unsubscribed" : "Subscribed"}
        </div>
        <div className="text-[11px] text-stone-500 truncate">
          {unsubscribed
            ? <>Won&apos;t receive marketing e-shots{dateLabel ? ` · since ${dateLabel}` : ""}{sourceLabel ? ` · ${sourceLabel}` : ""}</>
            : <>{email} will be included in your next campaign</>}
        </div>
      </div>
      <button
        type="button"
        onClick={onToggle}
        disabled={busy}
        data-testid={`${testid}-toggle`}
        className={`shrink-0 px-3 py-1.5 rounded-md text-[10px] font-bold uppercase tracking-wider transition ${
          unsubscribed
            ? "bg-stone-950 text-white hover:bg-stone-800"
            : "bg-red-600 text-white hover:bg-red-700"
        } disabled:opacity-50`}
      >
        {unsubscribed ? "Re-subscribe" : "Mark unsubscribed"}
      </button>
    </div>
  );
}


// ----------------------------------------------------------------------------
// MarketingCrmPanel — the right-column sales-pipeline tracker.
// Lives inside this file because every field directly mutates the modal's
// ``form`` state via the shared `set(k, v)` helper. Pure UI: persistence
// happens when the user hits "Save changes" on the parent modal.
// ----------------------------------------------------------------------------
function MarketingCrmPanel({ form, set, marketingEnabled, onOpenMarketingPlus, clientSaved }) {
  const status = form.lead_status || "";
  const selectedOption = LEAD_STATUS_OPTIONS.find((o) => o.value === status);
  const tone = selectedOption ? TONE_STYLES[selectedOption.tone] : TONE_STYLES.grey;
  const followUpOn = !!form.follow_up_required;
  return (
    <section
      className="bg-white border border-stone-200 rounded-xl overflow-hidden"
      data-testid="t-plus-marketing-panel"
    >
      <header className="px-4 py-3 bg-stone-950 text-[#dddd16] flex items-center gap-2">
        <Megaphone className="w-4 h-4" />
        <div className="text-[11px] uppercase tracking-[0.25em] font-black">Marketing</div>
      </header>
      <div className="p-4 space-y-3">
        {/* LEAD STATUS — coloured dropdown with chip */}
        <div>
          <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-stone-600 mb-1 block">
            Lead Status
          </label>
          <div className={`relative rounded-lg border-2 ${tone.border} ${tone.fill}`}>
            <span className={`absolute left-3 top-1/2 -translate-y-1/2 w-3 h-3 rounded-full ${tone.dot} border border-stone-400`}></span>
            <select
              value={status}
              onChange={(e) => set("lead_status", e.target.value)}
              data-testid="t-plus-lead-status"
              className="w-full pl-9 pr-3 py-2 text-sm bg-transparent appearance-none focus:outline-none cursor-pointer font-medium"
            >
              <option value="">— select status —</option>
              {LEAD_STATUS_OPTIONS.map((o) => {
                const t = TONE_STYLES[o.tone];
                return (
                  <option
                    key={o.value}
                    value={o.value}
                    style={{ backgroundColor: t.optionBg, color: t.optionFg, fontWeight: 600 }}
                  >
                    {o.label}
                  </option>
                );
              })}
            </select>
          </div>
          {selectedOption && (
            <div className={`mt-1 inline-flex items-center gap-1.5 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded border ${tone.chip}`}>
              <span className={`w-2 h-2 rounded-full ${tone.dot}`}></span>
              {selectedOption.label}
            </div>
          )}
        </div>

        {/* LAST CONTACT — date + method side by side */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-stone-600 mb-1 block">
              Last Contact
            </label>
            <input
              type="date"
              value={form.last_contact_date || ""}
              onChange={(e) => set("last_contact_date", e.target.value)}
              data-testid="t-plus-last-contact-date"
              className="w-full px-3 py-2 text-sm bg-stone-50 border border-stone-300 rounded-lg focus:outline-none focus:border-stone-950"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-stone-600 mb-1 block">
              Method
            </label>
            <select
              value={form.last_contact_method || ""}
              onChange={(e) => set("last_contact_method", e.target.value)}
              data-testid="t-plus-last-contact-method"
              className="w-full px-3 py-2 text-sm bg-stone-50 border border-stone-300 rounded-lg focus:outline-none focus:border-stone-950"
            >
              <option value="">— select —</option>
              {CONTACT_METHOD_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* FOLLOW UP — Yes/No toggle */}
        <div>
          <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-stone-600 mb-1 block">
            Follow Up Required?
          </label>
          <div className="inline-flex rounded-lg overflow-hidden border border-stone-300">
            <button
              type="button"
              onClick={() => set("follow_up_required", true)}
              data-testid="t-plus-follow-up-yes"
              className={`px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider transition-colors ${followUpOn ? "bg-amber-400 text-stone-950" : "bg-white text-stone-700 hover:bg-stone-50"}`}
            >
              {followUpOn && <Check className="inline-block w-3 h-3 mr-1 -mt-0.5" />}Yes
            </button>
            <button
              type="button"
              onClick={() => {
                set("follow_up_required", false);
                // Clear follow-up fields when toggled off — keeps the
                // saved doc tidy and avoids ghost reminders.
                set("follow_up_date", "");
                set("follow_up_notes", "");
              }}
              data-testid="t-plus-follow-up-no"
              className={`px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider border-l border-stone-300 transition-colors ${!followUpOn ? "bg-stone-950 text-white" : "bg-white text-stone-700 hover:bg-stone-50"}`}
            >
              {!followUpOn && <Check className="inline-block w-3 h-3 mr-1 -mt-0.5" />}No
            </button>
          </div>
        </div>

        {followUpOn && (
          <>
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-stone-600 mb-1 block">
                Follow Up Date
              </label>
              <input
                type="date"
                value={form.follow_up_date || ""}
                onChange={(e) => set("follow_up_date", e.target.value)}
                data-testid="t-plus-follow-up-date"
                className="w-full px-3 py-2 text-sm bg-stone-50 border border-stone-300 rounded-lg focus:outline-none focus:border-stone-950"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-stone-600 mb-1 block">
                Follow Up Notes
              </label>
              <textarea
                value={form.follow_up_notes || ""}
                onChange={(e) => set("follow_up_notes", e.target.value)}
                rows={3}
                placeholder="What's the next step? Topics to discuss, agreed next call…"
                data-testid="t-plus-follow-up-notes"
                className="w-full px-3 py-2 text-sm bg-stone-50 border border-stone-300 rounded-lg focus:outline-none focus:border-stone-950"
              />
            </div>
          </>
        )}
      </div>
    </section>
  );
}


// ---------------------------------------------------------------------------
// HqNoteSection — HQ notes history against a CQC entry.
// * Admin view: textarea + "Save HQ note" button that APPENDS a new
//   entry every time (never overwrites). Success clears the input,
//   flashes a green confirmation and inserts the new entry at the top
//   of the visible history. Errors surface in a red banner. Each entry
//   in the history gets a small "Delete" button so typos can be
//   cleaned up.
// * Franchisee view: same list rendered read-only, headed
//   "Notes from Creative Mojo HQ", with an empty-state message when
//   there are none.
// ---------------------------------------------------------------------------
function HqNoteSection({ hqEntries = [], editable, onSave, onEntryDelete, source, homeId }) {
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [flash, setFlash] = useState("");

  const save = async () => {
    if (!onSave || !source || !homeId) {
      setErr("Missing home reference — reopen the modal and try again.");
      return;
    }
    const trimmed = draft.trim();
    if (!trimmed) {
      setErr("Type a note before saving.");
      return;
    }
    setSaving(true); setErr(""); setFlash("");
    try {
      await onSave(source, homeId, trimmed);
      setDraft("");
      setFlash("HQ note saved.");
      window.setTimeout(() => setFlash(""), 2500);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "Could not save HQ note.");
    } finally { setSaving(false); }
  };

  const deleteEntry = async (id) => {
    if (!onEntryDelete) return;
    if (!window.confirm("Delete this HQ note entry? This can't be undone.")) return;
    try { await onEntryDelete(id); } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "Could not delete entry.");
    }
  };

  const entries = Array.isArray(hqEntries) ? hqEntries : [];

  // Read-only view (franchisee portal) — no textarea, no editing.
  if (!editable) {
    return (
      <div
        className="border border-amber-300 bg-amber-50 rounded-lg p-3"
        data-testid="hq-note-section-readonly"
      >
        <div className="flex items-center justify-between mb-2">
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-amber-900 flex items-center gap-1.5">
            Notes from Creative Mojo HQ
            <span className="px-1.5 py-0.5 text-[9px] bg-amber-200 text-amber-900 rounded uppercase tracking-wider">Read-only</span>
          </div>
          {entries.length > 0 && (
            <span className="text-[10px] text-amber-800 tabular-nums">
              {entries.length} entr{entries.length === 1 ? "y" : "ies"}
            </span>
          )}
        </div>
        {entries.length === 0 ? (
          <div className="text-xs italic text-amber-800/80 text-center py-3">
            No notes from Creative Mojo HQ.
          </div>
        ) : (
          <ul className="space-y-2">
            {entries.map((e) => (
              <li key={e.id}
                  data-testid={`hq-note-entry-${e.id}`}
                  className="bg-white/70 border border-amber-200 rounded-md px-3 py-2.5">
                <div className="flex items-start justify-between gap-3">
                  <div className="text-[11px] font-bold text-amber-900">
                    {e.updated_by_name || e.updated_by || "HQ"}
                  </div>
                  <div className="text-[10px] text-amber-700 tabular-nums shrink-0">
                    {e.updated_at ? new Date(e.updated_at).toLocaleString("en-GB", { dateStyle: "short", timeStyle: "short", timeZone: "Europe/London" }) : ""}
                  </div>
                </div>
                <div className="text-sm text-stone-950 whitespace-pre-wrap mt-1 leading-relaxed">{e.note}</div>
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  }

  // Editable view (admin franchisee page).
  return (
    <div
      className="border border-amber-300 bg-amber-50 rounded-lg p-3"
      data-testid="hq-note-section"
    >
      <div className="flex items-center justify-between mb-1.5">
        <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-amber-900 flex items-center gap-1.5">
          HQ Note
          <span className="text-amber-700 normal-case tracking-normal font-normal">· appended to a history</span>
        </div>
        <span className="text-[10px] text-amber-800 tabular-nums">
          {entries.length} entr{entries.length === 1 ? "y" : "ies"}
        </span>
      </div>
      <textarea
        value={draft}
        onChange={(e) => { setDraft(e.target.value); setErr(""); }}
        rows={3}
        placeholder="Add a new HQ note — the franchisee will see the running history on their MyTerritory+ portal."
        data-testid="hq-note-textarea"
        className="w-full px-3 py-2 text-sm bg-white border border-amber-300 rounded-md focus:outline-none focus:border-amber-500"
      />
      {err && (
        <div data-testid="hq-note-error" className="mt-2 text-xs bg-red-50 border border-red-200 text-red-700 rounded-md px-3 py-2">
          {err}
        </div>
      )}
      {flash && !err && (
        <div data-testid="hq-note-flash" className="mt-2 text-xs bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-md px-3 py-2">
          {flash}
        </div>
      )}
      <div className="mt-2 flex items-center justify-end">
        <button
          type="button"
          onClick={save}
          disabled={saving || !draft.trim()}
          data-testid="hq-note-save"
          className="px-3 py-1.5 text-[10px] uppercase tracking-wider font-bold rounded bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-40"
        >
          {saving ? "Saving…" : "Save HQ note"}
        </button>
      </div>

      {/* History — newest first. Each entry shows who added it and when.
          Admins can delete individual entries; franchisees never see
          this widget (they get the read-only variant above). */}
      <div className="mt-3 space-y-2" data-testid="hq-note-history">
        {entries.length === 0 ? (
          <div className="text-[11px] italic text-amber-800/80 border border-dashed border-amber-200 rounded-md px-3 py-3 text-center">
            No HQ notes on file yet — the first save above will land here.
          </div>
        ) : entries.map((e) => (
          <div key={e.id}
               data-testid={`hq-note-entry-${e.id}`}
               className="bg-white/70 border border-amber-200 rounded-md px-3 py-2.5">
            <div className="flex items-start justify-between gap-3">
              <div className="text-[11px] font-bold text-amber-900">
                {e.updated_by_name || e.updated_by || "HQ"}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <div className="text-[10px] text-amber-700 tabular-nums">
                  {e.updated_at ? new Date(e.updated_at).toLocaleString("en-GB", { dateStyle: "short", timeStyle: "short", timeZone: "Europe/London" }) : ""}
                </div>
                {onEntryDelete && (
                  <button
                    type="button"
                    onClick={() => deleteEntry(e.id)}
                    className="text-[10px] uppercase tracking-wider text-red-600 hover:text-red-800"
                    data-testid={`hq-note-entry-delete-${e.id}`}
                  >Delete</button>
                )}
              </div>
            </div>
            <div className="text-sm text-stone-950 whitespace-pre-wrap mt-1 leading-relaxed">{e.note}</div>
          </div>
        ))}
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------
// QuickNoteInput — single-line "Add a note…" input above the running
// Notes log. Enter (or blur with content) saves via the parent's
// ``onSave`` handler which PATCHes the client and lets the server
// prepend a UK-local timestamp to the running ``notes`` field.
// Uncontrolled so it never re-renders while typing; the field is
// cleared imperatively after a successful save.
// ---------------------------------------------------------------------
function QuickNoteInput({ saving, onSave }) {
  const inputRef = useRef(null);

  const commit = async (opts = {}) => {
    const el = inputRef.current;
    if (!el) return;
    const text = (el.value || "").trim();
    if (!text) return;
    const result = await onSave(text);
    if (result?.ok) {
      el.value = "";
      if (opts.refocus) el.focus();
    }
  };

  return (
    <div className="mb-2 flex items-stretch gap-2" data-testid="t-plus-quick-note-row">
      <input
        ref={inputRef}
        type="text"
        placeholder="Add a note… (press Enter to save)"
        disabled={saving}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            commit({ refocus: true });
          } else if (e.key === "Escape") {
            e.preventDefault();
            e.currentTarget.value = "";
            e.currentTarget.blur();
          }
        }}
        onBlur={() => commit()}
        data-testid="t-plus-quick-note-input"
        className="flex-1 px-3 py-2 text-sm bg-white border border-stone-300 rounded-lg focus:outline-none focus:border-stone-950 disabled:bg-stone-100 disabled:text-stone-500"
      />
      <button
        type="button"
        onClick={() => commit({ refocus: true })}
        disabled={saving}
        data-testid="t-plus-quick-note-save"
        title="Save this note (Enter also works)"
        className="px-3 py-2 rounded-lg bg-stone-950 text-white text-[10px] font-bold uppercase tracking-wider hover:bg-stone-800 disabled:bg-stone-400 transition-colors"
      >
        {saving ? "Saving…" : "Save note"}
      </button>
    </div>
  );
}


// ---------------------------------------------------------------------
// QuickNoteSavedIndicator — mirrors the admin CRM's "Saved just now /
// Xm ago" label so franchisees get the same immediate feedback after
// a quick-note append. Falls through to nothing when no save has
// happened this session and no ``notes_updated_at`` exists.
// ---------------------------------------------------------------------
function QuickNoteSavedIndicator({ savedAt, saving }) {
  if (saving) {
    return (
      <span
        className="text-[10px] text-stone-500 flex items-center gap-1.5"
        data-testid="t-plus-notes-saving"
      >
        <Loader2 className="w-3 h-3 animate-spin" /> Saving…
      </span>
    );
  }
  if (!savedAt) {
    return (
      <span className="text-[10px] text-stone-400" data-testid="t-plus-notes-hint">
        Type above · press Enter to save
      </span>
    );
  }
  const label = (() => {
    try {
      const d = new Date(savedAt);
      const diff = Math.max(0, Math.floor((Date.now() - d.getTime()) / 1000));
      if (diff < 60) return "just now";
      if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
      if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
      return d.toLocaleDateString("en-GB");
    } catch {
      return "";
    }
  })();
  return (
    <span
      className="text-[10px] text-stone-500"
      title={`Last saved ${savedAt}`}
      data-testid="t-plus-notes-saved"
    >
      Saved {label}
    </span>
  );
}
